import React from "react";
import {
  AbsoluteFill,
  Composition,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

export const ClaudeMascotComposition: React.FC = () => {
  return (
    <Composition
      id="ClaudeMascot"
      component={ClaudeMascotAnimation}
      durationInFrames={150} // 5.0 seconds @ 30fps
      fps={30}
      width={1080}
      height={1920}
    />
  );
};

// Official Claude SVG Path (Anthropic Sunburst / Asterisk)
const CLAUDE_PATH =
  "m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z";

export const ClaudeMascotAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Color Palette (Anthropic Editorial Style)
  const CLAUDE_ORANGE = "#D97757"; // Signature terracotta
  const BG_CREAM = "#FAF7F2";
  const TEXT_DARK = "#1F1E1D";
  const TEXT_MUTED = "#73726E";
  const WARM_BORDER = "#EAE4D9";

  // 1. Mascot Entrance (Bouncy Spring + Overshoot)
  const mascotScale = spring({
    frame: frame - 10,
    fps,
    config: { damping: 11, stiffness: 130, mass: 0.7 },
  });

  const mascotRotate = spring({
    frame: frame - 10,
    fps,
    config: { damping: 13, stiffness: 100 },
  });

  // Mascot Idle Floating & Breathing
  const floatY = Math.sin(frame / 12) * 10;
  const breatheScale = 1 + Math.sin(frame / 16) * 0.03;

  // Mascot Eye Blink logic (at frame 45-52 and frame 105-112)
  const isBlinking1 = frame >= 45 && frame <= 50;
  const isBlinking2 = frame >= 105 && frame <= 110;
  const eyeScaleY = isBlinking1 || isBlinking2 ? 0.1 : 1;

  // 2. Title & Kinetic Typography Entrance
  const titleSpring = spring({
    frame: frame - 25,
    fps,
    config: { damping: 12, stiffness: 120 },
  });

  const badgeSpring = spring({
    frame: frame - 5,
    fps,
    config: { damping: 14, stiffness: 110 },
  });

  // 3. Floating Feature Pills Entrance (staggered)
  const pill1Spring = spring({ frame: frame - 60, fps, config: { damping: 12 } });
  const pill2Spring = spring({ frame: frame - 70, fps, config: { damping: 12 } });
  const pill3Spring = spring({ frame: frame - 80, fps, config: { damping: 12 } });

  // 4. Outro / Call To Action
  const ctaSpring = spring({
    frame: frame - 95,
    fps,
    config: { damping: 11, stiffness: 120 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: BG_CREAM,
        fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
        color: TEXT_DARK,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "90px 60px 80px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Warm ambient background glow */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          height: 900,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(217,119,87,0.14) 0%, rgba(250,247,242,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Subtle organic grid */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />

      {/* TOP: Header Pill Badge */}
      <div
        style={{
          opacity: badgeSpring,
          transform: `translateY(${(1 - badgeSpring) * -30}px)`,
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "#FFFFFF",
            border: `1.5px solid ${WARM_BORDER}`,
            padding: "14px 28px",
            borderRadius: 999,
            boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: CLAUDE_ORANGE,
              boxShadow: `0 0 10px ${CLAUDE_ORANGE}`,
            }}
          />
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_DARK,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            Claude × Remotion
          </span>
        </div>
      </div>

      {/* CENTER: The Animated Claude Mascot */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 20,
          flex: 1,
        }}
      >
        {/* Mascot Avatar Container */}
        <div
          style={{
            position: "relative",
            width: 380,
            height: 380,
            transform: `scale(${mascotScale * breatheScale}) rotate(${interpolate(
              mascotRotate,
              [0, 1],
              [-90, 0]
            )}deg) translateY(${floatY}px)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            filter: "drop-shadow(0 25px 50px rgba(217,119,87,0.28))",
          }}
        >
          {/* Claude Sunburst Star SVG */}
          <svg
            viewBox="0 0 24 24"
            style={{
              width: "100%",
              height: "100%",
              fill: CLAUDE_ORANGE,
            }}
          >
            <path d={CLAUDE_PATH} />
          </svg>

          {/* Cute Animated Mascot Face Overlay */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              pointerEvents: "none",
            }}
          >
            {/* Eyes Container */}
            <div style={{ display: "flex", gap: 36, alignItems: "center" }}>
              {/* Left Eye */}
              <div
                style={{
                  width: 18,
                  height: 24,
                  backgroundColor: "#FFFFFF",
                  borderRadius: "50%",
                  transform: `scaleY(${eyeScaleY})`,
                  position: "relative",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 3,
                    width: 7,
                    height: 7,
                    backgroundColor: "#FFFFFF",
                    borderRadius: "50%",
                  }}
                />
              </div>

              {/* Right Eye */}
              <div
                style={{
                  width: 18,
                  height: 24,
                  backgroundColor: "#FFFFFF",
                  borderRadius: "50%",
                  transform: `scaleY(${eyeScaleY})`,
                  position: "relative",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 3,
                    width: 7,
                    height: 7,
                    backgroundColor: "#FFFFFF",
                    borderRadius: "50%",
                  }}
                />
              </div>
            </div>

            {/* Cute Smile */}
            <div
              style={{
                width: 22,
                height: 11,
                borderBottom: "3.5px solid #FFFFFF",
                borderRadius: "0 0 20px 20px",
                marginTop: -2,
              }}
            />
          </div>
        </div>

        {/* Big Clean Kinetic Title */}
        <div
          style={{
            marginTop: 40,
            textAlign: "center",
            opacity: titleSpring,
            transform: `translateY(${(1 - titleSpring) * 40}px)`,
          }}
        >
          <h1
            style={{
              fontSize: 88,
              fontWeight: 900,
              letterSpacing: -2,
              margin: 0,
              lineHeight: 1.05,
              color: TEXT_DARK,
            }}
          >
            Meet Claude
          </h1>
          <p
            style={{
              fontSize: 32,
              fontWeight: 600,
              color: CLAUDE_ORANGE,
              margin: "12px 0 0",
              letterSpacing: -0.5,
            }}
          >
            Prompt to Motion Graphics
          </p>
        </div>

        {/* Feature Staggered Pills */}
        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 40,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              opacity: pill1Spring,
              transform: `scale(${pill1Spring}) translateY(${(1 - pill1Spring) * 20}px)`,
              backgroundColor: "#FFFFFF",
              border: `1px solid ${WARM_BORDER}`,
              padding: "14px 26px",
              borderRadius: 999,
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_DARK,
              boxShadow: "0 4px 14px rgba(0,0,0,0.03)",
            }}
          >
            ⚡ React + TypeScript Video
          </div>

          <div
            style={{
              opacity: pill2Spring,
              transform: `scale(${pill2Spring}) translateY(${(1 - pill2Spring) * 20}px)`,
              backgroundColor: "#FFFFFF",
              border: `1px solid ${WARM_BORDER}`,
              padding: "14px 26px",
              borderRadius: 999,
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_DARK,
              boxShadow: "0 4px 14px rgba(0,0,0,0.03)",
            }}
          >
            🎯 Spring Physics Easing
          </div>

          <div
            style={{
              opacity: pill3Spring,
              transform: `scale(${pill3Spring}) translateY(${(1 - pill3Spring) * 20}px)`,
              backgroundColor: "#FFFFFF",
              border: `1px solid ${WARM_BORDER}`,
              padding: "14px 26px",
              borderRadius: 999,
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_DARK,
              boxShadow: "0 4px 14px rgba(0,0,0,0.03)",
            }}
          >
            🎥 60fps Native MP4
          </div>
        </div>
      </div>

      {/* BOTTOM: Elegant Card CTA */}
      <div
        style={{
          width: "100%",
          opacity: ctaSpring,
          transform: `translateY(${(1 - ctaSpring) * 50}px)`,
          zIndex: 20,
        }}
      >
        <div
          style={{
            backgroundColor: "#FFFFFF",
            border: `1.5px solid ${WARM_BORDER}`,
            borderRadius: 36,
            padding: "36px 44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 20px 50px rgba(0,0,0,0.06)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 20,
                color: TEXT_MUTED,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
              }}
            >
              Built with
            </div>
            <div
              style={{
                fontSize: 42,
                fontWeight: 900,
                color: TEXT_DARK,
                letterSpacing: -1,
              }}
            >
              Remotion + Claude Code
            </div>
          </div>

          <div
            style={{
              backgroundColor: CLAUDE_ORANGE,
              color: "#FFFFFF",
              padding: "20px 36px",
              borderRadius: 22,
              fontSize: 26,
              fontWeight: 800,
              boxShadow: "0 10px 25px rgba(217,119,87,0.4)",
            }}
          >
            Start Prompting ➜
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
